import * as PIXI from 'pixi.js';
import * as React from 'react';
import './App.css';
import GameConfig from './game/GameConfig';
import PixiCanvas from './Pixi';
import QueryStringModel from './queryString/QueryStringModel';
import Connection from './Connection/index';
import Firebase from './Firebase/firebase';
import debounce from 'lodash/debounce';
import flow from 'lodash/flow';
import {withAuthentication, withAuthUser} from './Session/context';
import {Action} from './Action';
import {ServerLobby} from './ServerLobby';
import {Connecting} from './Connecting';
import {GameLobby} from './GameLobby';
import {LeaderboardEntry, LoginProps, ServerState, GameLobbyState} from './AppTypes';
import {GameModeName} from './common/modes/GameMode';

interface ReceivedMessage {
    actions: Action[];
}

type AppStateName = 'server-lobby' | 'game-lobby' | 'game';

interface AppState {
    connected: boolean;
    retryConnectionAt?: number;
    spritesLoaded: boolean;

    topPlayers?: LeaderboardEntry[];
    login: LoginProps;

    name: AppStateName;
    server?: ServerState;
    lobby?: GameLobbyState;
    actions: Action[];
}

interface Props {
    firebase: Firebase;
    authUser: firebase.User | null;
}

class App extends React.Component<Props, AppState> {
    public state: AppState;
    private forceUpdateTimeout?: NodeJS.Timer;

    public constructor(props: Readonly<Props>) {
        super(props);
        this.state = {
            connected: false,
            spritesLoaded: false,
            login: {
                name: '',
                modeName: 'classic',
            },
            name: 'server-lobby',
            actions: [],
        };
        QueryStringModel.parse();
        this.loadLeaderboard = debounce(this.loadLeaderboard.bind(this), 10000, {leading: true});
        this.handleSetProfile = debounce(this.handleSetProfile.bind(this), 500);
    }

    public componentDidMount(): void {
        Connection.connect(this.handleConnected, this.onMessage, this.handleDisconnected);

        this.signIn();

        // Load sprites when we first load the page
        PIXI.settings.SCALE_MODE = PIXI.SCALE_MODES.NEAREST;
        PIXI.settings.MIPMAP_TEXTURES = false;
        PIXI.loader.add(GameConfig.spritesheetUrl).load(this.onSpritesLoaded);
    }

    public componentWillUnmount(): void {
        if (this.forceUpdateTimeout) {
            clearTimeout(this.forceUpdateTimeout);
        }
    }

    public render(): JSX.Element {
        return (
            <div className={`App ${this.state.name === 'server-lobby' ? '' : 'full-size'}`}>
                {this.makeView()}
            </div>
        );
    }

    public makeView(): JSX.Element {
        if (!this.state.connected) {
            this.forceUpdateTimeout = setTimeout(() => this.forceUpdate(), 1001);
            return (
                <Connecting
                    retryConnectionAt={this.state.retryConnectionAt}
                    onRetryConnection={this.retryConnection}
                />
            );
        } else if (this.state.name === 'server-lobby') {
            return (
                <ServerLobby
                    login={this.state.login}
                    server={this.state.server}
                    topPlayers={this.state.topPlayers}
                    onSetName={this.handleSetName}
                    onSetGameMode={this.handleSetGameMode}
                    onSelectMap={this.handleSetMapName}
                    onStartGame={this.handleStartGame}
                    onJoinGame={this.handleJoinGame}
                />
            );
        } else if (this.state.name === 'game-lobby') {
            return <GameLobby lobby={this.state.lobby} />;
        } else if (!this.state.spritesLoaded) {
            return <div>Loading...</div>;
        } else {
            return (
                <PixiCanvas
                    actions={this.state.actions}
                    onReturnToLobby={this.handleReturnToLobby}
                />
            );
        }
    }

    private handleSetName = (name: string) => {
        this.setState({...this.state, login: {...this.state.login, name}});
        this.handleSetProfile(name);
    };

    private handleSetGameMode = (modeName: GameModeName) => {
        const mapName = this.defaultMapNameForMode(modeName);
        this.setState({...this.state, login: {...this.state.login, modeName, mapName}});
    };

    private defaultMapNameForMode(modeName: GameModeName): string {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this.state.server!.maps!.filter(map => map.modeName === modeName)[0].name;
    }

    private handleSetMapName = (mapName: string) => {
        this.setState({...this.state, login: {...this.state.login, mapName}});
    };

    private handleStartGame = () => {
        if (this.state.login.mapName) {
            this.startGame(this.state.login.mapName);
        } else {
            // eslint-disable-next-line no-console
            console.error('map name is not set');
        }
    };

    private handleJoinGame = (lobby: GameLobbyState) => {
        this.startGame(lobby.mapName);
    };

    private async startGame(mapName: string): Promise<void> {
        if (!this.props.authUser) return;

        const name = this.state.login.name;
        const idToken = await this.props.authUser.getIdToken(true);
        Connection.send('start', {name, idToken, mapName});
    }

    private handleReturnToLobby = () => {
        Connection.send('leave', {});
    };

    public componentDidUpdate(prevProps: Props, prevState: AppState): void {
        // Update the display name when it is changed; default to a random name when it is empty
        const {authUser} = this.props;
        if (authUser && prevProps.authUser !== authUser) {
            const name = authUser.displayName || this.randomPlayerName();
            if (!authUser.displayName) {
                this.handleSetProfile(name);
            }
            this.setState({...this.state, login: {...this.state.login, name}});
            this.loadLeaderboard();
        }

        // Update leaderboard when we go back to the login page
        if (this.state.name === 'server-lobby' && prevState.name !== 'server-lobby') {
            // Wait a moment so the server can register a potential win that may have just happend
            setTimeout(() => this.loadLeaderboard(), 1000);
        }
    }

    private onSpritesLoaded = () => {
        this.setState({...this.state, spritesLoaded: true});
    };

    private handleConnected = () => {
        this.setState({...this.state, connected: true});
    };

    private onMessage = (data: string) => {
        const {actions: appActions} = JSON.parse(data) as ReceivedMessage;
        if (!appActions) return;
        const {name, lobby, server, gameActions: actions} = this.processActions(appActions);
        const login: LoginProps = {...this.state.login, mapName: this.selectedMapName(server)};
        this.setState({...this.state, name, login, lobby, server, actions});
    };

    private selectedMapName(server?: ServerState): string | undefined {
        return (
            this.state.login.mapName || (server && server.maps ? server.maps[0].name : undefined)
        );
    }

    private processActions(
        actions: Action[]
    ): {name: AppStateName; lobby?: GameLobbyState; server?: ServerState; gameActions: Action[]} {
        let {name, lobby, server} = this.state;
        const gameActions: Action[] = [];
        actions.forEach(action => {
            switch (action.kind) {
                case 'set-server-state':
                    name = 'server-lobby';
                    server = action.server;
                    lobby = undefined;
                    gameActions.length = 0;
                    break;

                case 'set-lobby-state':
                    name = 'game-lobby';
                    lobby = action.lobby;
                    server = undefined;
                    gameActions.length = 0;
                    break;

                default:
                    name = 'game';
                    gameActions.push(action);
                    server = undefined;
                    lobby = undefined;
                    break;
            }
        });
        return {name, lobby, server, gameActions};
    }

    private handleDisconnected = (retryIn: number) => {
        this.setState({...this.state, connected: false, retryConnectionAt: Date.now() + retryIn});
    };

    // debounced
    private async loadLeaderboard(): Promise<void> {
        const result = await this.props.firebase.db
            .collection('playerstats')
            .where('isBot', '==', false)
            .orderBy('winCount', 'desc')
            .limit(5)
            .get();
        const topPlayers: LeaderboardEntry[] = [];
        result.forEach(doc => {
            const {name, winCount, gameCount} = doc.data() as LeaderboardEntry;
            topPlayers.push({name, winCount, gameCount});
        });
        this.setState({...this.state, topPlayers});
    }

    // debounced
    private handleSetProfile(displayName: string): void {
        if (!this.props.authUser) return;

        this.props.authUser.updateProfile({
            displayName,
            photoURL: this.props.authUser.photoURL,
        });
    }

    private async signIn(): Promise<void> {
        if (!this.props.authUser) {
            try {
                await this.props.firebase.auth.signInAnonymously();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        }
    }

    private retryConnection(): void {
        Connection.attemptReconnect();
    }

    private randomPlayerName(): string {
        const number = Math.floor(Math.random() * 100000);
        return `Player-${number}`;
    }
}

export default flow([withAuthUser, withAuthentication])(App);



// WEBPACK FOOTER //
// ./src/App.tsx